import type { GenEnum } from '@bufbuild/protobuf/codegenv2'
import { type ConnectError } from '@connectrpc/connect'
import {
  type ErrorInfo,
  ErrorInfoSchema,
} from '@ebalance/proto/google/rpc/error_details_pb'
import { type TFunction } from 'i18next'

/**
 * Generic consumer of the standard `google.rpc.ErrorInfo` status detail
 * (docs/adr/structured-app-error-standard-errorinfo-envelope.md decision 1,
 * superseding the retired self-defined `bffuser.v1.AppErrorInfo` 2-layer
 * mirror). `ErrorInfo.reason` stays a generic wire string (docs/adr/
 * structured-app-error-typed-reason.md decision 1): the per-master typed
 * proto enum is the SoT vocabulary both backend (`.String()`) and frontend
 * (generated TS enum) derive from, but is not carried on the wire as an
 * enum field.
 *
 * `ErrorInfo` never carries a field (decision 2 of the standard-envelope
 * ADR): domain owners (one file per screen/tab, e.g.
 * `AdminSingularDay/models/serverError.ts`) supply the generated enum's
 * schema + a `Record<Enum, AppErrorInfoReasonEntry | undefined>` where each
 * entry carries the i18n key and, when the violation is field-scoped, the
 * frontend's own camelCase React Hook Form field name. This helper does the
 * mechanical parts common to every domain: reading the detail, resolving
 * the wire string back to the typed enum via protobuf-es reflection
 * (`reasonFromWireString`), looking up the key, and classifying
 * field-scoped vs operation-scoped from the registry entry alone.
 */
export type AppErrorInfoResolution =
  | { kind: 'field'; field: string; message: string }
  | { kind: 'operation'; message: string }

/**
 * Per-reason registry entry: the i18n key for the localized message, plus
 * `field` (the frontend's own camelCase field name, e.g. `"bgCode"`) when
 * the violation is field-scoped. Absent `field` = operation-scoped (top-
 * level alert). This is the sole SoT for reason → (field | operation)
 * routing now that the envelope itself never carries field — the backend
 * intentionally does not know frontend field naming, so no snake_case →
 * camelCase conversion happens here (decision 2's "frontend 自身の camelCase
 * field 名を registry に直書き").
 */
export type AppErrorInfoReasonEntry = {
  key: string
  field?: string
}

/**
 * Resolves a wire `reason` string (the proto enum's value name, e.g.
 * `"BG_MASTER_ERROR_REASON_BG_CODE_INVALID_FORMAT"`) back to the
 * generated TS enum's numeric value via protobuf-es reflection
 * (`schema.values`, sourced from the same proto descriptor the generated
 * enum comes from — not a hand-written string↔enum map, per decision 3's
 * "手書き literal map 禁止" — the seam is deliberately outside protoc-gen-es
 * because protoc-gen-es strips the enum's shared prefix from TS member
 * names, so a plain `wire as Enum` cast or `Record[wireString]` index
 * would not line up with the wire value). Returns `undefined` for an
 * unrecognized wire string (unknown / stale reason), which callers treat
 * the same as "no reason key configured" (existing code-based fallback,
 * decision D).
 */
function reasonFromWireString<E extends number>(
  schema: GenEnum<E>,
  wire: string,
): E | undefined {
  return schema.values.find((v) => v.name === wire)?.number as E | undefined
}

/**
 * Reads the `google.rpc.ErrorInfo` Connect error detail (if present) off
 * `err`, resolves its wire `reason` string back to the generated
 * per-master enum (`schema`) via `reasonFromWireString`, and looks the
 * result up in `reasonKeys` (a domain-scoped `Enum` → entry map owned by
 * the caller — `Record<Enum, AppErrorInfoReasonEntry | undefined>` so tsc
 * forces every enum member, including `UNSPECIFIED`, to have an explicit
 * entry). Returns `undefined` when no `ErrorInfo` detail is attached, the
 * `reason` does not resolve to a known enum value, or resolves to a value
 * whose `reasonKeys` entry is `undefined` — the caller falls back to the
 * existing gRPC-code-based generic message (`mapErrorToMessage` /
 * `mapConnectErrorToMessage`), matching decision D's non-breaking
 * contract.
 */
export function resolveAppErrorInfoMessage<E extends number>(
  err: ConnectError,
  schema: GenEnum<E>,
  reasonKeys: Record<E, AppErrorInfoReasonEntry | undefined>,
  t: TFunction,
  /**
   * Caller-supplied i18n interpolation values (`t(key, interpolationValues)`),
   * for registries whose message template embeds caller-known runtime data
   * the static registry entry cannot carry (例: 重複エラーの `{{bgCode}}` /
   * `{{submitterCode}}`, or the currently-selected method's display name).
   * Merged with `info.metadata` (docs/adr/
   * structured-app-error-standard-errorinfo-envelope.md decision c, spec
   * AC-OMT-028's `{code, partnerName}`) below — `info.metadata` supplies the
   * base values so every reason carrying metadata gets automatic
   * interpolation without each caller re-extracting it, and
   * `interpolationValues` can override/extend them for values `info` cannot
   * carry. Unused placeholders in a given entry's template are ignored by
   * i18next, so passing this uniformly to every resolution is safe.
   *
   * Callers MUST source these from the fresh submitted values (RHF's
   * `handleSubmit` callback argument), never from outer render-scope state,
   * to avoid a stale-closure mismatch between the displayed value and what
   * was actually submitted (see useCrudModalForm.handleSave's contract,
   * occto-plan-master-reason-registry 設計書 D1 / N3).
   */
  interpolationValues?: Record<string, unknown>,
): AppErrorInfoResolution | undefined {
  // detail の読み出しと reason の reflection 解決は `readAppErrorInfo` が唯一の
  // 入口 (= envelope の解釈を 2 箇所に複製しない)。 ここは registry lookup と
  // 文言解決だけを足す。
  const info = readAppErrorInfo(err, schema)
  if (info === undefined) {
    return undefined
  }
  const entry = reasonKeys[info.reason]
  if (entry === undefined) {
    return undefined
  }
  const message = t(entry.key, { ...info.metadata, ...interpolationValues })
  return entry.field !== undefined
    ? { kind: 'field', field: entry.field, message }
    : { kind: 'operation', message }
}

/**
 * Reads the `google.rpc.ErrorInfo` Connect error detail (if present) off
 * `err` and resolves its wire `reason` string back to the generated
 * per-master enum (`schema`), the same reflection-based resolution
 * `resolveAppErrorInfoMessage` uses internally. Returns the resolved `reason`
 * plus the raw `metadata` map (never `undefined`; empty when `info.metadata`
 * itself is empty) instead of a rendered message.
 *
 * For callers that need to build their own structured data from a reason's
 * metadata (rather than only interpolate it into one fixed i18n template) —
 * e.g. AC-BIN-015's per-axis 入札制限 violation, where the caller turns
 * `{qtyMaxLimit: "50"}` into a `ViolationSection` detail line alongside data
 * (actual value, area, koma) it already holds from the submitted row, not
 * from the error at all.
 */
export function readAppErrorInfo<E extends number>(
  err: ConnectError,
  schema: GenEnum<E>,
): { reason: E; metadata: Record<string, string> } | undefined {
  let info: ErrorInfo | undefined
  try {
    info = err.findDetails(ErrorInfoSchema)[0]
  } catch {
    return undefined
  }
  if (info === undefined) {
    return undefined
  }
  const reasonValue = reasonFromWireString(schema, info.reason)
  if (reasonValue === undefined) {
    return undefined
  }
  return { reason: reasonValue, metadata: info.metadata }
}
